import { supabase } from '../lib/supabase';

/**
 * Upload shipment photo to Supabase Storage
 * @param {File} file - The image file to upload
 * @param {string} shipmentId - The shipment ID
 * @returns {Promise<string>} The file path in storage
 */
export const uploadShipmentPhoto = async (file, shipmentId) => {
  try {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${shipmentId}-${Date.now()}.${fileExt}`;
    const filePath = `${shipmentId}/${fileName}`;

    const { error: uploadError } = await supabase?.storage?.from('shipment-photos')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      throw uploadError;
    }

    return filePath;
  } catch (error) {
    console.error('Error uploading shipment photo:', error);
    throw new Error(`Échec du téléversement de la photo: ${error.message}`);
  }
};

/**
 * Upload additional photo to photos_urls array
 * @param {File} file - The image file to upload
 * @param {string} shipmentId - The shipment ID
 * @returns {Promise<string>} The file path in storage
 */
export const uploadAdditionalPhoto = async (file, shipmentId) => {
  try {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `extra-${shipmentId}-${Date.now()}.${fileExt}`;
    const filePath = `${shipmentId}/${fileName}`;

    const { error: uploadError } = await supabase
      ?.storage
      ?.from('shipment-photos')
      ?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      throw uploadError;
    }

    return filePath;
  } catch (error) {
    console.error('Error uploading additional photo:', error);
    throw new Error(`Échec du téléversement de la photo: ${error.message}`);
  }
};

/**
 * Get signed URL for shipment photo (private bucket)
 * @param {string} filePath - The file path in storage
 * @returns {Promise<string>} Signed URL for the photo
 */
export const getShipmentPhotoUrl = async (filePath) => {
  try {
    if (!filePath) return null;

    const { data, error } = await supabase?.storage?.from('shipment-photos')?.createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) {
      // Silently handle "Object not found" errors - file may have been deleted
      if (error?.message?.includes('Object not found') || error?.statusCode === '404') {
        return null;
      }
      throw error;
    }

    return data?.signedUrl;
  } catch (error) {
    // Only log non-404 errors to console
    if (!error?.message?.includes('Object not found')) {
      console.error('Error getting shipment photo URL:', error);
    }
    return null;
  }
};

/**
 * Delete shipment photo from storage
 * @param {string} filePath - The file path to delete
 */
export const deleteShipmentPhoto = async (filePath) => {
  try {
    if (!filePath) return;

    const { error } = await supabase?.storage?.from('shipment-photos')?.remove([filePath]);

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error deleting shipment photo:', error);
    throw error;
  }
};

/**
 * Upload shipment document (PDF) to Supabase Storage
 * @param {File} file - The PDF file to upload
 * @param {string} shipmentId - The shipment ID
 * @param {string} fileType - Type of document (invoice, douane, autre)
 * @returns {Promise<Object>} Object with filePath and fileSize
 */
export const uploadShipmentDocument = async (file, shipmentId, fileType) => {
  try {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${fileType}-${shipmentId}-${Date.now()}.${fileExt}`;
    const filePath = `${shipmentId}/${fileName}`;

    // ✅ FIX: Check if file is an image type
    const isImage = file?.type?.startsWith('image/');
    const bucketName = isImage ? 'shipment-photos' : 'shipment-documents';

    console.log(`📤 Uploading ${isImage ? 'image' : 'document'} to bucket: ${bucketName}`, {
      fileType: file?.type,
      fileName,
      bucketName
    });

    const { error: uploadError } = await supabase?.storage?.from(bucketName)?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file?.type // Explicitly set content type from file
      });

    if (uploadError) {
      throw uploadError;
    }

    return {
      filePath,
      fileName: file?.name,
      fileSize: file?.size,
      bucketName // Return bucket name so we know where it was stored
    };
  } catch (error) {
    console.error('Error uploading shipment document:', error);
    throw new Error(`Échec du téléversement du document: ${error.message}`);
  }
};

/**
 * Get signed URL for shipment document (private bucket)
 * @param {string} filePath - The file path in storage
 * @returns {Promise<string>} Signed URL for the document
 */
export const getShipmentDocumentUrl = async (filePath) => {
  try {
    if (!filePath) return null;

    const { data, error } = await supabase?.storage?.from('shipment-documents')?.createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) {
      // Silently handle "Object not found" errors - file may have been deleted
      if (error?.message?.includes('Object not found') || error?.statusCode === '404') {
        return null;
      }
      throw error;
    }

    return data?.signedUrl;
  } catch (error) {
    // Only log non-404 errors to console
    if (!error?.message?.includes('Object not found')) {
      console.error('Error getting shipment document URL:', error);
    }
    return null;
  }
};

/**
 * Delete shipment document from storage
 * @param {string} filePath - The file path to delete
 */
export const deleteShipmentDocument = async (filePath) => {
  try {
    if (!filePath) return;

    const { error } = await supabase?.storage?.from('shipment-documents')?.remove([filePath]);

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error deleting shipment document:', error);
    throw error;
  }
};

/**
 * Save document metadata to database
 * @param {string} shipmentId - The shipment ID
 * @param {string} fileType - Type of document
 * @param {string} filePath - Path in storage
 * @param {string} fileName - Original file name
 * @param {number} fileSize - File size in bytes
 * @param {string} bucketName - Bucket where file is stored
 */
export const saveDocumentMetadata = async (shipmentId, fileType, filePath, fileName, fileSize, bucketName) => {
  try {
    const { data, error } = await supabase?.from('shipment_files')?.insert({
        shipment_id: shipmentId,
        file_type: fileType,
        file_url: filePath,
        file_name: fileName,
        file_size: fileSize,
        bucket_name: bucketName // Store which bucket this file is in
      })?.select()?.single();

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error saving document metadata:', error);
    throw error;
  }
};

/**
 * Save photo metadata to shipment_files with file_type='photo'
 * @param {string} shipmentId - The shipment ID
 * @param {string} filePath - Path in storage (bucket: shipment-photos)
 * @param {string} fileName - Original file name
 * @param {number} fileSize - File size in bytes (optional)
 */
export const savePhotoMetadata = async (shipmentId, filePath, fileName, fileSize = null) => {
  try {
    if (!shipmentId || !filePath) {
      console.warn('[savePhotoMetadata] Missing shipmentId or filePath', { shipmentId, filePath });
      return null;
    }

    // Generate a signed URL to store as file_url
    let fileUrl = filePath;
    try {
      const { data: urlData } = await supabase?.storage
        ?.from('shipment-photos')
        ?.createSignedUrl(filePath, 60 * 60 * 24 * 365); // 1 year
      if (urlData?.signedUrl) {
        fileUrl = urlData?.signedUrl;
      }
    } catch (urlErr) {
      console.warn('[savePhotoMetadata] Could not generate signed URL, storing path instead:', urlErr);
    }

    const { data, error } = await supabase?.from('shipment_files')?.insert({
      shipment_id: shipmentId,
      file_type: 'photo',
      file_url: fileUrl,
      file_name: fileName || filePath?.split('/')?.pop(),
      file_size: fileSize,
      bucket_name: 'shipment-photos'
    })?.select()?.single();

    if (error) {
      console.error('[savePhotoMetadata] Error saving photo metadata to shipment_files:', error);
      throw error;
    }

    console.log('[savePhotoMetadata] ✅ Photo metadata saved to shipment_files:', data?.id);
    return data;
  } catch (error) {
    console.error('[savePhotoMetadata] Error in savePhotoMetadata:', error);
    throw error;
  }
};

/**
 * Get all documents for a shipment
 * @param {string} shipmentId - The shipment ID
 * @returns {Promise<Array>} Array of document records with signed URLs
 */
export const getShipmentDocuments = async (shipmentId) => {
  try {
    // Validate shipmentId before making database query
    if (!shipmentId || shipmentId === 'undefined') {
      console.warn('getShipmentDocuments called with invalid shipmentId:', shipmentId);
      return [];
    }

    const { data, error } = await supabase?.from('shipment_files')?.select('*')?.eq('shipment_id', shipmentId)?.order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    // Get signed URLs for all documents
    const documentsWithUrls = await Promise.all(
      (data || [])?.map(async (doc) => {
        // ✅ FIX: Use correct bucket based on stored bucket_name
        const bucketName = doc?.bucket_name || 'shipment-documents'; // Default to documents for old records
        
        const { data: urlData, error: urlError } = await supabase
          ?.storage
          ?.from(bucketName)
          ?.createSignedUrl(doc?.file_url, 3600);

        if (urlError) {
          console.warn(`Error getting URL for ${doc?.file_name}:`, urlError);
          return { ...doc, downloadUrl: null };
        }

        return {
          ...doc,
          downloadUrl: urlData?.signedUrl
        };
      })
    );

    // Filter out documents where URL generation failed (file doesn't exist in storage)
    return documentsWithUrls?.filter(doc => doc?.downloadUrl !== null);
  } catch (error) {
    console.error('Error getting shipment documents:', error);
    return [];
  }
};

/**
 * Delete document metadata from database
 * @param {string} documentId - The document ID
 */
export const deleteDocumentMetadata = async (documentId) => {
  try {
    const { error } = await supabase?.from('shipment_files')?.delete()?.eq('id', documentId);

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error deleting document metadata:', error);
    throw error;
  }
};

/**
 * Shipment Storage Service - Centralized object with all storage methods
 */
export const shipmentStorageService = {
  // Photo methods
  uploadPhoto: uploadShipmentPhoto,
  uploadAdditionalPhoto: uploadAdditionalPhoto,
  getPhotoUrl: getShipmentPhotoUrl,
  deletePhoto: deleteShipmentPhoto,
  savePhotoMetadata: savePhotoMetadata,
  
  // Document methods
  uploadDocument: async (shipmentId, file, fileType) => {
    try {
      // Upload file to storage
      const { filePath, fileName, fileSize, bucketName } = await uploadShipmentDocument(file, shipmentId, fileType);
      
      // Save metadata to database with bucket information
      await saveDocumentMetadata(shipmentId, fileType, filePath, fileName, fileSize, bucketName);
      
      return { filePath, fileName, fileSize, bucketName };
    } catch (error) {
      console.error('Error in uploadDocument:', error);
      throw error;
    }
  },
  
  getDocuments: getShipmentDocuments,
  
  getDocumentUrl: getShipmentDocumentUrl,
  
  deleteDocument: async (documentId, fileUrl, bucketName = 'shipment-documents') => {
    try {
      // Delete from correct bucket
      const { error: storageError } = await supabase
        ?.storage
        ?.from(bucketName)
        ?.remove([fileUrl]);

      if (storageError) {
        throw storageError;
      }
      
      // Delete metadata from database
      await deleteDocumentMetadata(documentId);
    } catch (error) {
      console.error('Error in deleteDocument:', error);
      throw error;
    }
  }
};